import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Button } from 'antd';
import { DragOutlined, EditOutlined } from '@ant-design/icons';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getUserProfile } from '../../utils/authHelper';
import moment from 'moment';
import CreateSummaryDialog from './CreateSummaryDialog';

// Helper function to convert order object to sorted array
const convertOrderObjectToSortedArray = (orderObject) => {
    if (!orderObject || Object.keys(orderObject).length === 0) {
        return [];
    }
    
    return Object.entries(orderObject)
        .map(([summaryId, orderNumber]) => ({ 
            summaryId: parseInt(summaryId), 
            order: orderNumber 
        }))
        .sort((a, b) => a.order - b.order);
};

// Helper function to order summaries based on user settings
const orderSummariesByUserSettings = (summaries, userSettingsOrder) => {
    if (!userSettingsOrder || Object.keys(userSettingsOrder).length === 0) {
        return summaries;
    }
    
    const orderArray = convertOrderObjectToSortedArray(userSettingsOrder);
    const orderedSummaries = orderArray
        .map(orderItem => summaries.find(summary => summary.id === orderItem.summaryId))
        .filter(Boolean);
    
    // Add any summaries not in user settings to the end
    const userSettingsSummaryIds = new Set(Object.keys(userSettingsOrder).map(id => parseInt(id)));
    const remainingSummaries = summaries.filter(summary => !userSettingsSummaryIds.has(summary.id));
    
    return [...orderedSummaries, ...remainingSummaries];
};

// Helper function to create summary order object from data source
const createSummaryOrderObject = (dataSource) => {
    const summaryOrder = {};
    dataSource.forEach((item, index) => {
        summaryOrder[item.id] = index + 1;
    });
    return summaryOrder;
};

// Sortable item component for drag and drop
const SortableItem = ({ summary, index, userSettingsSummaryOrder, embedded, onEditSummary, disabled = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: summary.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // Get order from userSettingsSummaryOrder object
    const getUserSettingsOrder = () => {
        if (!userSettingsSummaryOrder) return '-';
        return userSettingsSummaryOrder[summary.id.toString()] || '-';
    };

    // Check if summary is created by current user or only used by them
    const userProfile = getUserProfile();
    const currentUserId = userProfile?.sub;
    const isCreatedByUser = summary.createdBy === currentUserId;

    return (
        <div style={{ position: 'relative' }}>
            {/* Edit button outside the draggable area */}
            {embedded && isCreatedByUser && (
                <Button 
                    type="primary"
                    shape="circle"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                        console.log('Edit button clicked for summary:', summary.id);
                        onEditSummary(summary);
                    }}
                    style={{ 
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '28px',
                        height: '28px',
                        zIndex: 10,
                        transform: style.transform
                    }}
                />
            )}
            
            {/* Draggable content */}
            <div
                ref={setNodeRef}
                style={{
                    ...style,
                    padding: '12px',
                    margin: '4px 0',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    backgroundColor: isCreatedByUser ? '#fff' : '#f5f5f5',
                    opacity: isCreatedByUser ? 1 : 0.8,
                    cursor: disabled ? 'default' : 'grab',
                    position: 'relative',
                    paddingTop: embedded && isCreatedByUser ? '40px' : '12px'
                }}
                {...attributes}
                {...(disabled ? {} : listeners)}
            >

            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%'
            }}>
                {!disabled && <DragOutlined style={{ cursor: 'grab', color: '#999', marginRight: '8px' }} />}
                <span style={{ fontWeight: 'bold' }}>{index + 1}.</span>
                <span style={{ color: '#ccc', marginLeft: '8px', fontSize: '12px' }}>
                    ({getUserSettingsOrder()})
                </span>
                <div style={{ marginLeft: '16px', flex: 1 }}>
                    <div className="arabic-font summary-order-title">
                        {summary.titleUrdu || 'No summary title'}
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '11px', color: '#999' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ 
                                width: '12px', 
                                height: '12px', 
                                border: '1px solid #d9d9d9',
                                borderRadius: '2px',
                                backgroundColor: summary.needPlaceForAdvice === true ? '#1890ff' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {summary.needPlaceForAdvice === true && (
                                    <span style={{ color: 'white', fontSize: '8px', fontWeight: 'bold' }}>✓</span>
                                )}
                            </div>
                            <span style={{ color: '#666' }}>Extra Place</span>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>
                        <span style={{ fontWeight: '500', color: '#666' }}>Created: </span>
                        {summary.created ? moment(summary.created).format('MMM DD, YYYY') : 'Unknown date'}
                    </div>
                    {!isCreatedByUser && summary.createdByName && (
                        <div style={{ fontSize: '11px', color: '#999' }}>
                            <span style={{ fontWeight: '500', color: '#666' }}>By: </span>
                            {summary.createdByName}
                        </div>
                    )}
                </div>
            </div>
        </div>
        </div>
    );
};

// Mode toggle buttons component
const ModeToggleButtons = ({ isUserMode, onModeChange }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <Button 
            type={isUserMode ? "default" : "primary"} 
            onClick={() => onModeChange(false)}
            style={{ flex: 1, marginRight: '8px' }}
        >
            Stack Mode
        </Button>
        <Button 
            type={isUserMode ? "primary" : "default"} 
            onClick={() => onModeChange(true)}
            style={{ flex: 1, marginLeft: '8px' }}
        >
            User Mode
        </Button>
    </div>
);

// Footer buttons component
const FooterButtons = ({ 
    isUserMode, 
    userSettingsSummaryOrder, 
    dataSource, 
    onCancel, 
    onReset, 
    onSaveToSettings, 
    onSaveToStack,
    disabled = false
}) => {
    const handleSave = () => {
        const newSummaryOrder = createSummaryOrderObject(dataSource);
        
        if (isUserMode && onSaveToSettings) {
            onSaveToSettings(newSummaryOrder);
        } else if (!isUserMode && onSaveToStack) {
            onSaveToStack(newSummaryOrder);
        }
    };

    return [
        <Button key="cancel" onClick={onCancel}>
            Cancel
        </Button>,
        <Button 
            key="reset" 
            onClick={onReset} 
            disabled={disabled || !userSettingsSummaryOrder}
        >
            Reset to User Settings
        </Button>,
        <Button 
            key="save" 
            type="primary" 
            onClick={handleSave}
            disabled={disabled}
        >
            {isUserMode ? 'Save as Permanent' : 'Save to Stack'}
        </Button>
    ];
};

// Embedded title buttons component
const EmbeddedTitleButtons = ({ 
    isUserMode, 
    userSettingsSummaryOrder, 
    dataSource, 
    onReset, 
    onSaveToSettings, 
    onSaveToStack,
    getCurrentDataSource,
    disabled = false
}) => {
    const handleSave = () => {
        // Use getCurrentDataSource to get the most up-to-date data
        const currentDataSource = getCurrentDataSource ? getCurrentDataSource() : dataSource;
        const newSummaryOrder = createSummaryOrderObject(currentDataSource);
         
        if (isUserMode && onSaveToSettings) {
            onSaveToSettings(newSummaryOrder);
        } else if (!isUserMode && onSaveToStack) {
            onSaveToStack(newSummaryOrder);
        }
    };

    return (
        <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
                size="small"
                onClick={onReset} 
                disabled={disabled || !userSettingsSummaryOrder}
            >
                Reset
            </Button>
            <Button 
                size="small"
                type="primary" 
                onClick={handleSave}
                disabled={disabled}
            >
                {isUserMode ? 'Save' : 'Save to Stack'}
            </Button>
        </div>
    );
};

/**
 * SummaryOrderView component for reordering summaries
 * @param {boolean} visible - Modal visibility
 * @param {function} onCancel - Cancel callback
 * @param {Array} summariesList - List of summary objects for current stack
 * @param {Array} allUserSummaries - List of all summary objects created by or used by the user
 * @param {Object} userSettingsSummaryOrder - User's preferred summary order (object {summaryId: orderNumber})
 * @param {Object} currentSummaryOrder - Current summary order (object {summaryId: orderNumber})
 * @param {function} onSaveToSettings - Callback to save order to user settings
 * @param {function} onSaveToStack - Callback to save order to stack
 */
const SummaryOrderView = ({ 
    visible, 
    onCancel, 
    summariesList, 
    allUserSummaries,
    userSettingsSummaryOrder, 
    currentSummaryOrder,
    onSaveToSettings,
    onSaveToStack,
    embedded = false,
    userModeOnly = false,
    onGetTitleContent,
    disabled = false,
    searchTerm = '',
    onSummaryUpdated = null
}) => {
    const [stackModeData, setStackModeData] = useState([]);
    const [userModeData, setUserModeData] = useState([]);
    const [isUserMode, setIsUserMode] = useState(userModeOnly); // Start in User Mode if userModeOnly is true
    const [hasLocalChanges, setHasLocalChanges] = useState(false);
    
    // Dialog states for editing summaries
    const [showEditSummaryDialog, setShowEditSummaryDialog] = useState(false);
    const [selectedSummary, setSelectedSummary] = useState(null);
    
    // Use ref to always get the current data source
    const currentDataSourceRef = useRef([]);
    const userModeDataRef = useRef([]);
    const stackModeDataRef = useRef([]);

    // Get current data source based on mode - memoized to prevent infinite loops
    const currentDataSource = useMemo(() => {
        return isUserMode ? userModeData : stackModeData;
    }, [isUserMode, userModeData, stackModeData]);
    
    // Filter data source based on search term
    const filteredDataSource = useMemo(() => {
        if (!searchTerm || searchTerm.trim() === '') {
            return currentDataSource;
        }
        
        const searchLower = searchTerm.toLowerCase();
        return currentDataSource.filter(summary => 
            (summary.title && summary.title.toLowerCase().includes(searchLower)) ||
            (summary.titleUrdu && summary.titleUrdu.toLowerCase().includes(searchLower)) ||
            (summary.content && summary.content.toLowerCase().includes(searchLower)) ||
            (summary.contentUrdu && summary.contentUrdu.toLowerCase().includes(searchLower))
        );
    }, [currentDataSource, searchTerm]);
    
    // Update refs whenever data changes
    useEffect(() => {
        currentDataSourceRef.current = currentDataSource;
        userModeDataRef.current = userModeData;
        stackModeDataRef.current = stackModeData;
    }, [currentDataSource, userModeData, stackModeData]);

    // Initialize stack mode data source
    useEffect(() => {
        if (!summariesList || summariesList.length === 0) return;
        
        // Don't reset data when disabled (filtered) or when there are local changes
        if (disabled || hasLocalChanges) return;

        let orderedSummaries;
        
        if (currentSummaryOrder && Object.keys(currentSummaryOrder).length > 0) {
            // Use current stack order if available
            orderedSummaries = orderSummariesByUserSettings(summariesList, currentSummaryOrder);
        } else if (userSettingsSummaryOrder && Object.keys(userSettingsSummaryOrder).length > 0) {
            // Fall back to user settings order
            orderedSummaries = orderSummariesByUserSettings(summariesList, userSettingsSummaryOrder);
        } else {
            // Use original order
            orderedSummaries = summariesList;
        }
        
        setStackModeData(orderedSummaries);
    }, [summariesList, currentSummaryOrder, userSettingsSummaryOrder, disabled, hasLocalChanges]);

    // Initialize user mode data source
    useEffect(() => {
        if (!allUserSummaries || allUserSummaries.length === 0) {
            setUserModeData([]);
            return;
        }
        
        // Don't reset data when disabled (filtered) or when there are local changes
        if (disabled || hasLocalChanges) {
            return;
        }

        // Only initialize if userModeData is empty
        if (userModeData.length > 0) {
            return;
        }
        const orderedSummaries = orderSummariesByUserSettings(allUserSummaries, userSettingsSummaryOrder);
        setUserModeData(orderedSummaries);
    }, [allUserSummaries, userSettingsSummaryOrder, disabled, hasLocalChanges]);

    // Ensure userModeData is initialized when userModeOnly is true
    useEffect(() => {
        if (userModeOnly && allUserSummaries && allUserSummaries.length > 0 && userModeData.length === 0) {
            const orderedSummaries = orderSummariesByUserSettings(allUserSummaries, userSettingsSummaryOrder);
            setUserModeData(orderedSummaries);
        }
    }, [userModeOnly, allUserSummaries, userSettingsSummaryOrder, userModeData.length]);

    // Update local state when allUserSummaries changes (e.g., after summary update)
    useEffect(() => {
        if (userModeOnly && allUserSummaries && allUserSummaries.length > 0) {
            const orderedSummaries = orderSummariesByUserSettings(allUserSummaries, userSettingsSummaryOrder);
            setUserModeData(orderedSummaries);
        }
    }, [allUserSummaries, userSettingsSummaryOrder, userModeOnly]);



    // DnD sensors configuration
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end event
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const updateDataSource = (items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);
            const newOrder = arrayMove(items, oldIndex, newIndex);
            return newOrder;
        };

        if (isUserMode) {
            const newUserModeData = updateDataSource(userModeData);
            setUserModeData(newUserModeData);
        } else {
            const newStackModeData = updateDataSource(stackModeData);
            setStackModeData(newStackModeData);
        }
        setHasLocalChanges(true);
    };

    // Handle reset to user settings
    const handleResetToUserSettings = () => {
        if (!userSettingsSummaryOrder) return;

        const resetData = (summaries) => {
            const orderedSummaries = orderSummariesByUserSettings(summaries, userSettingsSummaryOrder);
            return orderedSummaries.length > 0 ? orderedSummaries : summaries;
        };

        if (isUserMode) {
            setUserModeData(resetData(allUserSummaries));
        } else {
            setStackModeData(resetData(summariesList));
        }
    };

    // Track previous values to prevent unnecessary updates
    const prevValuesRef = useRef({
        isUserMode: null,
        userSettingsSummaryOrder: null,
        userModeDataLength: 0,
        stackModeDataLength: 0,
        disabled: null
    });

    // Call onGetTitleContent when in embedded mode - only when content actually changes
    useEffect(() => {
        if (embedded && onGetTitleContent) {
            const currentValues = {
                isUserMode,
                userSettingsSummaryOrder,
                userModeDataLength: userModeData.length,
                stackModeDataLength: stackModeData.length,
                disabled
            };

            const prevValues = prevValuesRef.current;
            
            // Only update if something meaningful has changed
            const hasChanged = 
                prevValues.isUserMode !== currentValues.isUserMode ||
                prevValues.userSettingsSummaryOrder !== currentValues.userSettingsSummaryOrder ||
                prevValues.userModeDataLength !== currentValues.userModeDataLength ||
                prevValues.stackModeDataLength !== currentValues.stackModeDataLength ||
                prevValues.disabled !== currentValues.disabled;

            if (hasChanged) {
                const titleContent = (
                    <EmbeddedTitleButtons
                        isUserMode={isUserMode}
                        userSettingsSummaryOrder={userSettingsSummaryOrder}
                        dataSource={isUserMode ? userModeData : stackModeData}
                        onReset={handleResetToUserSettings}
                        onSaveToSettings={onSaveToSettings}
                        onSaveToStack={onSaveToStack}
                        getCurrentDataSource={() => isUserMode ? userModeDataRef.current : stackModeDataRef.current}
                        disabled={disabled}
                    />
                );
                onGetTitleContent(titleContent);
                prevValuesRef.current = currentValues;
            }
        }
    }, [embedded, onGetTitleContent, isUserMode, userSettingsSummaryOrder, userModeData, stackModeData, disabled]);

    // Handle editing summaries
    const handleEditSummary = (summary) => {
        console.log('Editing summary:', summary);
        setSelectedSummary(summary);
        setShowEditSummaryDialog(true);
        console.log('Dialog state set to true, selectedSummary:', summary);
    };

    const content = (
        <>
            <div style={{ marginBottom: '16px' }}>
                {!userModeOnly && (
                    <ModeToggleButtons 
                        isUserMode={isUserMode} 
                        onModeChange={setIsUserMode} 
                    />
                )}
                {!embedded && (
                    <p style={{ margin: 0, color: '#666' }}>
                        Drag and drop items to reorder summaries. The order will be applied when printing.
                    </p>
                )}
            </div>

            
            <div style={{ maxHeight: embedded ? 'none' : '600px', overflow: embedded ? 'visible' : 'auto' }}>
                <DndContext
                    sensors={disabled ? [] : sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={disabled ? () => {} : handleDragEnd}
                >
                    <SortableContext
                        items={(isUserMode ? userModeData : stackModeData).map(item => item.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {filteredDataSource.map((summary, index) => {
                            // Find the actual index in the full data source for drag operations
                            const actualIndex = (isUserMode ? userModeData : stackModeData).findIndex(item => item.id === summary.id);
                            return (
                            <SortableItem 
                                key={summary.id} 
                                summary={summary} 
                                index={actualIndex} 
                                userSettingsSummaryOrder={userSettingsSummaryOrder}
                                embedded={embedded}
                                onEditSummary={handleEditSummary}
                                disabled={disabled}
                            />
                            );
                        })}
                    </SortableContext>
                </DndContext>
            </div>
        </>
    );

    if (embedded) {
        return (
            <>
                {content}
                <CreateSummaryDialog 
                    showModal={showEditSummaryDialog}
                    onHideModal={() => setShowEditSummaryDialog(false)}
                    summary={selectedSummary}
                    key={selectedSummary?.id || 'no-summary'}
                    onSummaryUpdated={onSummaryUpdated}
                />
            </>
        );
    }

    return (
        <Modal
            title="Set/Change Summary Order"
            open={visible}
            onCancel={onCancel}
            width={1200}
            style={{ top: 20 }}
            footer={
                <FooterButtons
                    isUserMode={isUserMode}
                    userSettingsSummaryOrder={userSettingsSummaryOrder}
                    dataSource={currentDataSource}
                    onCancel={onCancel}
                    onReset={handleResetToUserSettings}
                    onSaveToSettings={onSaveToSettings}
                    onSaveToStack={onSaveToStack}
                />
            }
        >
            {content}
            <CreateSummaryDialog 
                showModal={showEditSummaryDialog}
                onHideModal={() => setShowEditSummaryDialog(false)}
                summary={selectedSummary}
                onSummaryUpdated={onSummaryUpdated}
            />
        </Modal>
    );
};

export default SummaryOrderView; 